import api from '../../axios';
import {apiService} from '../apiService';

export const filesService = apiService.injectEndpoints({
    endpoints: (builder) =>
        ({
            uploadSchoolDocuments: builder.mutation({
                query: (files) => {
                    const formData = new FormData();

                    files.forEach(file => formData.append('files[]', file));

                    return ({
                        method: 'POST',
                        url: '/school/upload-documents',
                        data: formData,
                        headers: {
                            'Content-Type': 'multipart/form-data',
                        }
                    });
                },
                invalidatesTags: [{type: 'SchoolDocuments', id: 'LIST'}],
            }),
            uploadFormDocuments: builder.mutation({
                query: (files) => {
                    const formData = new FormData();

                    files.forEach(file => formData.append('files[]', file));

                    return ({
                        method: 'POST',
                        url: '/form/upload-documents',
                        data: formData,
                        headers: {
                            'Content-Type': 'multipart/form-data',
                        }
                    });
                },
                invalidatesTags: ['FormDocument'],
            }),
            deleteSchoolDocument: builder.mutation({
                query: (body) => ({
                    method: 'DELETE',
                    url: '/school/delete-document',
                    data: body
                }),
                invalidatesTags: [{type: 'SchoolDocuments', id: 'LIST'}],
            }),
            deleteFormDocument: builder.mutation({
                query: (body) => ({
                    method: 'DELETE',
                    url: '/form/delete-document',
                    data: body
                }),
                invalidatesTags: ['FormDocument'],
            }),
            getSchoolDocuments: builder.query({
                query: (body) => ({
                    url: '/school/documents',
                    method: 'POST',
                    data: body
                }),
                transformErrorResponse: (_) => ({message: 'Failed to retrieve documents'}),
                providesTags: (_) => [{type: 'SchoolDocuments', id: 'LIST'}]
            }),
            downloadSchoolDocument: builder.query({
                queryFn: async (documentId) => await downloadFileGet(`/school/download-document/${documentId}`)
            }),
            downloadFormDocument: builder.query({
                queryFn: async (documentId) => await downloadFileGet(`/form/download-document/${documentId}`)
            }),
            downloadProfileDocument: builder.query({
                queryFn: async ({
                                    studentId,
                                    documentId
                                }) => await downloadFileGet(`/student/${studentId}/download-document/${documentId}`)
            }),
            downloadProfileSenDocument: builder.query({
                queryFn: async ({
                                    studentId,
                                    documentId
                                }) => await downloadFileGet(`/student/${studentId}/sen/download-document/${documentId}`)
            }),
            downloadProfileVaccinationDocument: builder.query({
                queryFn: async ({
                                    studentId,
                                    documentId
                                }) => await downloadFileGet(`/student/${studentId}/medical/vaccination/download-document/${documentId}`)
            }),
            downloadProfileEhcpDocument: builder.query({
                queryFn: async ({
                                    studentId,
                                    documentId
                                }) => await downloadFileGet(`/student/${studentId}/ehcp/download-document/${documentId}`)
            }),
            downloadContactInvitesCsvReport: builder.query({
                queryFn: async ({body}) =>
                    await downloadFilePost('/school/contact-invites/csv', body)
            }),
            downloadContactInvitesXlsxReport: builder.query({
                queryFn: async ({body}) =>
                    await downloadFilePost('/school/contact-invites/xlsx', body)
            })
        })
});

const downloadFilePost = async (downloadUrl, body) => {
    const result = await api.post(downloadUrl, body, {responseType: 'blob'});
    return downloadFile(result);
};

const downloadFileGet = async (downloadUrl) => {
    const result = await api.get(downloadUrl, {responseType: 'blob'});
    return downloadFile(result);
};

const downloadFile = async (result) => {
    try {
        const blob = result.data;
        const filename = result.headers['content-disposition']
            ?.split('filename=')[1]
            ?.split(';')[0]
            ?.replace(/["']/g, '') ?? 'unknown.ext';

        return {data: {blob, filename}};
    } catch (error) {
        return {error: {status: 'DOWNLOAD_ERROR', error: error.message || 'Failed to download file.'}};
    }
};

export const {
    useUploadSchoolDocumentsMutation,
    useUploadFormDocumentsMutation,
    useDeleteSchoolDocumentMutation,
    useDeleteFormDocumentMutation,
    useLazyDownloadSchoolDocumentQuery,
    useLazyDownloadFormDocumentQuery,
    useLazyDownloadProfileDocumentQuery,
    useLazyDownloadProfileSenDocumentQuery,
    useLazyDownloadProfileVaccinationDocumentQuery,
    useLazyDownloadProfileEhcpDocumentQuery,
    useLazyDownloadContactInvitesCsvReportQuery,
    useLazyDownloadContactInvitesXlsxReportQuery,
    useGetSchoolDocumentsQuery
} = filesService;
